import { useMutation, useQuery } from "@tanstack/react-query";
import Card from "../../common/Card";
import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import { HeaderMenu } from "../../common/HeaderMenu";
import clsx from "clsx";
import styles from "./Purchases.module.scss";
import { FabButton } from "../../common/FabButton";
import React, { useEffect, useState } from "react";
import PurchaseEditor from "./PurchaseEditor";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import Form from "react-bootstrap/esm/Form";
import { useSearchParams } from "react-router";
import { useDebounce } from "use-debounce";
import { ItemActions } from "../../common/ItemActions";
import { DeleteModal } from "../../common/AppModal";
import useToast from "../../util/useToast";
import { AppPagination } from "../../common/AppPagination";
import { apiArchivePurchase, apiDeletePurchase, apiGetPurchases } from "../../api/ApiPurchases";
import Button from "../../common/Button";

const Purchases = () => {
  const {showToastSuccess} = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState(searchParams.get("filter") || "");
  const [debouncedFilter] = useDebounce(searchInputValue, 200);

  const [purchaseEditor, setPurchaseEditor] = useState({show: false, purchase: {}});
  const [deletePurchaseModal, setDeletePurchaseModal] = useState({show: false, purchase: null});

  useEffect(() => {
    const filter = debouncedFilter.trim();

    if (filter !== (searchParams.get("filter") || "")) {
      setSearchParams(filter ? { filter } : {});
    }
  }, [debouncedFilter]);

  const queryParams = {size: 50, page: +searchParams.get("page") || 0};

  if (searchParams.get("filter")) {
    queryParams["filter"] = searchParams.get("filter");
  }

  let purchasesQuery = useQuery({
    ...apiGetPurchases(queryParams),
    enabled: false
  });

  const deleteMutation = useMutation(apiDeletePurchase());
  const archiveMutation = useMutation(apiArchivePurchase());

  const purchases = purchasesQuery.data.content;
  const totalPages = purchasesQuery.data.page.totalPages;
  const totalElements = purchasesQuery.data.page.totalElements;

  return <Content>
    <ContentFixed>
      <HeaderMenu title="Matériel à acheter"/>
    </ContentFixed>

    <ContentScroll paddingTop withFab>
      <div className={styles.searchBar}>
        <Form.Control
          type="text"
          className={styles.searchInput}
          placeholder="Chercher"
          value={searchInputValue}
          onChange={e => setSearchInputValue(e.target.value)}
        />
      </div>

      <Card>
        <div className={clsx(styles.purchase, styles.header)}>
          <div className={styles.view}>
            <div className={styles.purchaseQuantity}>Quantité</div>
            <div className={styles.purchaseName}>Nom</div>
            <div className={styles.purchaseArchive}/>
          </div>
        </div>

        {purchases.length === 0 && <div className={styles.empty}>Pas de résultats</div>}

        {purchases.map(purchase => {
          const purchaseActions = [
            {
              type: "item",
              icon: "edit",
              label: "Modifier",
              onClick: () => setPurchaseEditor({show: true, mode: MODE_EDIT, purchase})
            },
            {type: "divider"},
            {
              type: "item",
              icon: "delete",
              label: "Supprimer",
              onClick: () => setDeletePurchaseModal({show: true, purchase})
            },
          ];

          return <div key={purchase.id} className={styles.purchase}>
            <div className={styles.view}>
              <div className={styles.purchaseQuantity}>{purchase.quantity}</div>
              <div className={styles.purchaseName}>{purchase.name}</div>
              <div className={styles.purchaseArchive}>
                <Button size="sm" onClick={() => {
                  archiveMutation.mutateAsync(purchase.id)
                    .then(() => purchasesQuery.refetch())
                    .then(() => showToastSuccess("Matériel à acheter archivé"));
                }}>Archiver</Button>
              </div>
            </div>

            <ItemActions actions={purchaseActions}/>
          </div>;
        })}
      </Card>

      <Card padding>
        <AppPagination
          itemName="élément"
          page={queryParams.page}
          totalElements={totalElements}
          totalPages={totalPages}
        />
      </Card>
    </ContentScroll>

    <FabButton onClick={() => setPurchaseEditor({show: true, mode: MODE_CREATE, purchase: {}})}/>

    <PurchaseEditor
      show={purchaseEditor.show}
      hide={() => setPurchaseEditor(prev => ({...prev, show: false}))}
      mode={purchaseEditor.mode}
      onSubmit={() => purchasesQuery.refetch()}
      purchase={purchaseEditor.purchase}
    />

    <DeleteModal
      show={deletePurchaseModal.show}
      title="Supprimer le matériel à acheter"
      onCancel={() => setDeletePurchaseModal({show: false})}
      onSubmit={() => {
        return deleteMutation.mutateAsync(deletePurchaseModal.purchase.id)
          .then(() => purchasesQuery.refetch())
          .then(() => {
            showToastSuccess("Matériel à acheter supprimé");

            setDeletePurchaseModal({show: false});
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce matériel à acheter ?
    </DeleteModal>
  </Content>;
};

export default Purchases;